import { captureError } from '../lib/sentry';

/**
 * Generate a short, user-displayable error reference id.
 * Format: `JU-${8-hex-char-suffix}`. The full uuid is logged to Sentry/console
 * (when captureError is wired up); the user-facing portion lets support
 * locate the corresponding log entry without exposing internals.
 *
 * Phase 13.2 (2026-04-29) — addresses OWASP A10:2025 "Mishandling of
 * Exceptional Conditions". Pair with sanitizeErrorMessage's `messageWithRef`.
 */
export function generateErrorId() {
  // Cheap pseudo-random; collision-safe enough for support reference (not a security id)
  const hex = Math.random().toString(16).slice(2, 10);
  return `JU-${hex}`;
}

/**
 * Translation key map — Phase 13.2 i18n-ready architecture.
 * For now, English-only via getMessage(). Future i18n migration swaps
 * getMessage() with i18next without touching call sites.
 */
const ERROR_KEYS = {
  permission_denied: 'errors.permission_denied',
  duplicate_record: 'errors.duplicate_record',
  not_found: 'errors.not_found',
  rate_limited: 'errors.rate_limited',
  network_offline: 'errors.network_offline',
  storage_upload: 'errors.storage_upload',
  session_expired: 'errors.session_expired',
  state_transition: 'errors.state_transition',
  required_field: 'errors.required_field',
  value_range: 'errors.value_range',
  foreign_key: 'errors.foreign_key',
  rls_violation: 'errors.rls_violation',
  rpc_error: 'errors.rpc_error',
  gdpr_export: 'errors.gdpr_export',
  auth_user_not_found: 'errors.auth_user_not_found',
  auth_invalid_login: 'errors.auth_invalid_login',
  auth_email_not_confirmed: 'errors.auth_email_not_confirmed',
  unknown_with_ref: 'errors.unknown_with_ref',
};

/**
 * English-only message map. Future i18n: swap with i18next.
 * `${ref}` placeholder is replaced with errorId when present.
 */
const ENGLISH_MESSAGES = {
  'errors.permission_denied': 'You do not have permission for this action.',
  'errors.duplicate_record': 'This record already exists.',
  'errors.not_found': 'Item not found.',
  'errors.rate_limited': 'Too many requests. Please wait a moment and try again.',
  'errors.network_offline': 'Network is slow or offline. Try again.',
  'errors.storage_upload': 'Couldn\'t upload file. Try again or contact support.',
  'errors.session_expired': 'Your session has expired. Please log in again.',
  'errors.state_transition': 'Invalid state transition. Please refresh and try again.',
  'errors.required_field': 'A required field is missing.',
  'errors.value_range': 'One or more values are outside the allowed range.',
  'errors.foreign_key': 'A referenced record could not be found.',
  'errors.rls_violation': 'You don\'t have access to this resource.',
  'errors.rpc_error': 'Something went wrong (ref: ${ref}). Try again or contact support@juuniai.com.',
  'errors.gdpr_export': 'Your data export couldn\'t be processed. Email don@juuniai.com to request manually.',
  'errors.auth_user_not_found': 'If an account exists, you\'ll receive instructions shortly.',
  'errors.auth_invalid_login': 'Email or password is incorrect.',
  'errors.auth_email_not_confirmed': 'Please verify your email before signing in.',
  'errors.unknown_with_ref': 'Something went wrong (ref: ${ref}). Try again or contact support@juuniai.com.',
};

/**
 * Resolve a translation key + params into a displayable English string.
 * Future i18n migration replaces with i18next translation lookup.
 */
function getMessage(messageKey, params = {}) {
  let msg = ENGLISH_MESSAGES[messageKey] || ENGLISH_MESSAGES['errors.unknown_with_ref'];
  for (const [k, v] of Object.entries(params)) {
    msg = msg.replace(`\${${k}}`, String(v));
  }
  return msg;
}

/**
 * Sanitize a Supabase / Postgres / network / Auth error for user display.
 *
 * Strips table names, column names, constraint names, RLS policy names, and
 * SQL details. Returns a user-safe English string. Pair with `generateErrorId()`
 * + Sentry capture for support-debuggable referencing.
 *
 * Phase 13.2 (2026-04-29) expansion: 15+ patterns now covered (was 7).
 * - RLS violations (mask table/policy names)
 * - Postgres error codes 42501 / 23505 / 23503 / PGRST116
 * - Network timeout / rate limit (429) / storage bucket / RPC errors
 * - GDPR-specific operations
 * - Supabase Auth errors (no enumeration leak per OWASP)
 *
 * @param {Error|string|object} error - error to sanitize
 * @param {object} [opts]
 * @param {string} [opts.errorId] - reference id (auto-generated if absent)
 * @returns {string} user-displayable English message
 */
export function sanitizeErrorMessage(error, opts = {}) {
  if (!error) return 'An unexpected error occurred.';
  const msg = typeof error === 'string' ? error : error.message || error.error || '';
  const code = (typeof error === 'object' && error?.code) || '';
  const status = (typeof error === 'object' && (error?.status || error?.statusCode)) || 0;

  const errorId = opts.errorId || generateErrorId();

  // Postgres error codes (most specific first)
  if (code === '42501' || msg.includes('insufficient_privilege')) {
    return getMessage(ERROR_KEYS.permission_denied);
  }
  if (code === '23505' || msg.includes('unique_violation') || msg.includes('duplicate') || msg.includes('already exists')) {
    return getMessage(ERROR_KEYS.duplicate_record);
  }
  if (code === '23503' || msg.includes('foreign_key_violation') || msg.includes('violates foreign key')) {
    return getMessage(ERROR_KEYS.foreign_key);
  }
  if (code === 'PGRST116' || msg.includes('multiple (or no) rows')) {
    return getMessage(ERROR_KEYS.not_found);
  }

  // RLS-specific (highest-stakes for info disclosure)
  if (msg.includes('row-level security') || msg.includes('RLS') || msg.includes('row level security')) {
    return getMessage(ERROR_KEYS.rls_violation);
  }
  if (msg.includes('permission denied')) {
    return getMessage(ERROR_KEYS.permission_denied);
  }

  // Network / rate limiting / storage
  if (status === 429 || msg.includes('rate limit') || msg.includes('Too Many Requests')) {
    return getMessage(ERROR_KEYS.rate_limited);
  }
  if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('timeout') || msg.includes('ETIMEDOUT')) {
    return getMessage(ERROR_KEYS.network_offline);
  }
  if (msg.includes('storage') || msg.includes('bucket') || msg.includes('upload')) {
    return getMessage(ERROR_KEYS.storage_upload);
  }

  // Auth flows (no enumeration leak per OWASP)
  if (msg.includes('User not found') || msg.includes('user not found')) {
    return getMessage(ERROR_KEYS.auth_user_not_found);
  }
  if (msg.includes('Invalid login') || msg.includes('invalid_login') || msg.includes('Invalid email or password')) {
    return getMessage(ERROR_KEYS.auth_invalid_login);
  }
  if (msg.includes('Email not confirmed') || msg.includes('email_not_confirmed')) {
    return getMessage(ERROR_KEYS.auth_email_not_confirmed);
  }
  if (msg.includes('JWT') || msg.includes('expired') && msg.includes('token')) {
    return getMessage(ERROR_KEYS.session_expired);
  }

  // GDPR-specific
  if (msg.toLowerCase().includes('gdpr') || msg.toLowerCase().includes('export user data')) {
    return getMessage(ERROR_KEYS.gdpr_export);
  }

  // Generic Postgres constraints
  if (msg.includes('violates not-null')) return getMessage(ERROR_KEYS.required_field);
  if (msg.includes('violates check')) return getMessage(ERROR_KEYS.value_range);

  // State machine (user-friendly text already)
  if (msg.includes('Invalid') && msg.includes('transition')) return msg;

  // RPC errors
  if (msg.includes('rpc') || code.startsWith('PGRST')) {
    return getMessage(ERROR_KEYS.rpc_error, { ref: errorId });
  }

  // Default: opaque message + ref id
  return getMessage(ERROR_KEYS.unknown_with_ref, { ref: errorId });
}

/**
 * safeSupabase — Safe wrappers for Supabase operations.
 *
 * safeQuery(queryPromise, label)  — For reads. Logs + returns { data, error }.
 * safeMutation(mutationPromise, label) — For writes. Logs + fires toast on failure.
 *
 * Usage:
 *   import { safeQuery, safeMutation } from '../utils/safeSupabase';
 *
 *   // Read:
 *   const { data, error } = await safeQuery(
 *     supabase.from('job').select('*').eq('status', 'Open'),
 *     'fetchOpenJobs'
 *   );
 *
 *   // Write:
 *   const { data, error } = await safeMutation(
 *     supabase.from('assignment').update({ status: 'Active' }).eq('id', assignId),
 *     'activateAssignment'
 *   );
 */

/**
 * Safe wrapper for Supabase read queries.
 * - Always returns { data, error } — never throws
 * - Logs errors with label context
 * - Returns { data: null, error } on failure
 */
export async function safeQuery(queryPromise, label = 'query') {
  try {
    const result = await queryPromise;
    if (result.error) {
      console.error(`[safeQuery:${label}]`, result.error.message);
      captureError(new Error(result.error.message), { label, type: 'query' });
    }
    return result;
  } catch (err) {
    console.error(`[safeQuery:${label}] Exception:`, err.message);
    captureError(err, { label, type: 'query' });
    return { data: null, error: err };
  }
}

/**
 * Safe wrapper for Supabase write mutations.
 * - Always returns { data, error } — never throws
 * - Logs errors with label context
 * - Fires global toast event on failure so user sees feedback
 * - Returns { data: null, error } on failure
 */
export async function safeMutation(mutationPromise, label = 'mutation') {
  try {
    const result = await mutationPromise;
    if (result.error) {
      console.error(`[safeMutation:${label}]`, result.error.message);
      captureError(new Error(result.error.message), { label, type: 'mutation' });
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('juuni:error', {
          detail: { message: sanitizeErrorMessage(result.error) },
        }));
      }
    }
    return result;
  } catch (err) {
    console.error(`[safeMutation:${label}] Exception:`, err.message);
    captureError(err, { label, type: 'mutation' });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('juuni:error', {
        detail: { message: sanitizeErrorMessage(err) },
      }));
    }
    return { data: null, error: err };
  }
}

/**
 * Check if the browser is online before attempting a write.
 * Returns true if online (safe to proceed), false if offline.
 * Fires toast if offline.
 */
export function requireOnline(label = 'operation') {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    console.warn(`[requireOnline:${label}] Blocked — browser is offline`);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('juuni:error', {
        detail: { message: "You're offline. This action requires an internet connection." },
      }));
    }
    return false;
  }
  return true;
}
